// Emoji data, recent emoji and image packs (16-emoji-packs.md): the standard emoji from emojibase
// (MIT) in the active language, loaded on demand; the person's most used emoji in account data;
// packs in the MSC2545 format. Outside a company the packs used everywhere are offered; in a company
// only the company's (the personal and room packs were candidates to drop).
import { EventType, type MatrixClient, type Room } from 'matrix-js-sdk';
import { useEffect, useState } from 'react';
import { compareMatches, matches } from '../core/filter';
import { setting } from '../core/settings';
import { currentLanguage } from '../i18n';
import { admin, inCompany } from '../session/admin';

export interface Emoji {
  char: string;
  label: string;
  shortcodes: string[];
  group: number;
  /** Tones 1–5 by index − 1, where every person has that tone. */
  tones?: string[];
}

export interface PackImage {
  shortcode: string;
  url: string;
  body?: string;
}
export interface Pack {
  /** "<room ID>|<state key>". */
  id: string;
  name: string;
  images: PackImage[];
}

/** desyr's eight groups from emojibase's (smileys and people together; components left out). */
const GROUPS: Record<number, number> = { 0: 0, 1: 0, 3: 1, 4: 2, 6: 3, 5: 4, 7: 5, 8: 6, 9: 7 };
export const GROUP_KEYS = ['smileys', 'animals', 'food', 'activity', 'travel', 'objects', 'symbols', 'flags'] as const;

interface Compact {
  unicode: string;
  label: string;
  hexcode: string;
  group?: number;
  skins?: Compact[];
}

let data: { list: Emoji[]; byChar: Map<string, Emoji> } | undefined;
let loading: Promise<NonNullable<typeof data>> | undefined;
const waiting = new Set<() => void>();
const strip = (s: string) => s.replace(/️/g, '');

/** Loads the standard emoji once (a failed load is tried again next time). */
export function loadEmoji(): Promise<NonNullable<typeof data>> {
  loading ??= (async () => {
    const de = currentLanguage() === 'de';
    const [compact, joy, base] = await Promise.all([
      (de ? import('emojibase-data/de/compact.json') : import('emojibase-data/en/compact.json')).then((m) => m.default as Compact[]),
      import('emojibase-data/en/shortcodes/joypixels.json').then((m) => m.default as Record<string, string | string[]>),
      import('emojibase-data/en/shortcodes/emojibase.json').then((m) => m.default as Record<string, string | string[]>),
    ]);
    const list: Emoji[] = [];
    for (const e of compact) {
      const codes = joy[e.hexcode] ?? base[e.hexcode];
      if (!codes || e.group === undefined || !(e.group in GROUPS)) continue;
      const tones = e.skins && [1, 2, 3, 4, 5].map((n) => e.skins!.find((s) => s.hexcode.split('-').filter((p) => /^1F3F[B-F]$/.test(p)).every((p) => p === `1F3F${'BCDEF'[n - 1]}`))?.unicode ?? e.unicode);
      list.push({ char: e.unicode, label: e.label, shortcodes: Array.isArray(codes) ? codes : [codes], group: GROUPS[e.group], tones });
    }
    data = { list, byChar: new Map(list.map((e) => [strip(e.char), e])) };
    waiting.forEach((w) => w());
    return data;
  })().catch((e) => {
    loading = undefined;
    throw e;
  });
  return loading;
}

/** The standard emoji once loaded (and drawing again when they arrive). */
export function useEmoji() {
  const [, set] = useState(0);
  useEffect(() => {
    if (data) return;
    const ready = () => set((n) => n + 1);
    waiting.add(ready);
    void loadEmoji().catch(() => {});
    return () => void waiting.delete(ready);
  }, []);
  return data;
}

/** In the background 4 s after start, when the browser is idle (16, Loading). */
export function preloadEmoji(): void {
  setTimeout(() => (window.requestIdleCallback ?? setTimeout)(() => void loadEmoji().catch(() => {}), { timeout: 10_000 }), 4000);
}

export const emojiByChar = (char: string) => data?.byChar.get(strip(char));
/** An emoji in the chosen skin tone (setting emoji-skin-tone, 0–5). */
export const toned = (e: Emoji) => {
  const tone = setting('emoji-skin-tone');
  return tone && e.tones ? e.tones[tone - 1] : e.char;
};

/** Standard emoji and pack images matching a search, best first (16, Search). */
export function searchEmoji(query: string, packs: Pack[]): (Emoji | PackImage)[] {
  const images = packs.flatMap((p) => p.images);
  // Each is ranked by its best-matching name: the label or any shortcode.
  const ranked = <T,>(items: T[], names: (item: T) => string[]) =>
    items
      .map((item) => ({ item, best: names(item).filter((n) => matches(n, query, true)).sort((a, b) => compareMatches(a, b, query))[0] }))
      .filter((x) => x.best !== undefined)
      .sort((a, b) => compareMatches(a.best, b.best, query))
      .map((x) => x.item);
  return [...ranked(images, (i) => [i.shortcode, i.body ?? '']), ...ranked(data?.list ?? [], (e) => [...e.shortcodes, e.label])].slice(0, 100);
}

const RECENT = 'chat.desyr.recent_emoji';
type Pair = [string, number];

const pairs = (client: MatrixClient): Pair[] =>
  ((client.getAccountData(RECENT as never)?.getContent().recent_emoji ?? []) as unknown[]).filter(
    (p): p is Pair => Array.isArray(p) && typeof p[0] === 'string' && Number.isInteger(p[1]) && (p[1] as number) >= 1,
  );

/** The person's most used emoji (known ones), most used first. */
export const recentEmoji = (client: MatrixClient, max: number) =>
  pairs(client)
    .sort((a, b) => b[1] - a[1])
    .map(([char]) => emojiByChar(char))
    .filter((e): e is Emoji => !!e)
    .slice(0, max);

/** Using an emoji: to the front, one more use, at most 100 kept (16, Recent emoji). */
export function useEmojiOnce(client: MatrixClient, char: string): void {
  const before = pairs(client);
  const old = before.find(([c]) => strip(c) === strip(char));
  const next: Pair[] = [[char, (old?.[1] ?? 0) + 1] as Pair, ...before.filter((p) => p !== old)].slice(0, 100);
  const content = client.getAccountData(RECENT as never)?.getContent() ?? {};
  void client.setAccountData(RECENT as never, { ...content, recent_emoji: next } as never).catch(() => {});
}

const PACK = 'im.ponies.room_emotes';
const COMPANY_TYPE = 'org.desyr.company_emoji';

/** A pack's images for one use (emoji or sticker), A–Z by shortcode. */
function imagesOf(content: Record<string, unknown>, use: 'emoticon' | 'sticker'): PackImage[] {
  const pack = (content.pack ?? {}) as { usage?: string[] };
  const usage = (u?: string[]) => (u?.length ? u : ['emoticon', 'sticker']);
  return Object.entries((content.images ?? {}) as Record<string, PackImage & { usage?: string[] }>)
    .filter(([, i]) => typeof i?.url === 'string' && usage(i.usage ?? pack.usage).includes(use))
    .map(([shortcode, i]) => ({ shortcode, url: i.url, body: i.body }))
    .sort((a, b) => a.shortcode.localeCompare(b.shortcode));
}

function packsOfRoom(room: Room, keys: string[] | undefined, use: 'emoticon' | 'sticker', fallback: string): Pack[] {
  return room.currentState
    .getStateEvents(PACK)
    .filter((ev) => !keys || keys.includes(ev.getStateKey() ?? ''))
    .map((ev) => {
      const content = ev.getContent();
      return { id: `${room.roomId}|${ev.getStateKey()}`, name: content.pack?.display_name ?? (room.name || fallback), images: imagesOf(content, use) };
    })
    .filter((p) => p.images.length);
}

/** Is this the company's emoji channel (by its creation type)? */
export const isCompanyEmojiRoom = (room: Room) => room.currentState.getStateEvents(EventType.RoomCreate, '')?.getContent().type === COMPANY_TYPE;

/** The company emoji channel's ID, from the admin service (set by the company module). */
let companyRoomId: string | null | undefined;
export const setCompanyEmojiRoom = (id: string | null | undefined) => (companyRoomId = id);
export const companyEmojiRoom = () => companyRoomId;

/** The packs offered for a use (16, Packs; The company's emoji). */
export function packsFor(client: MatrixClient, use: 'emoticon' | 'sticker', company: boolean, unknownName: string, companyName: string): Pack[] {
  if (company) {
    const room = companyRoomId ? client.getRoom(companyRoomId) : undefined;
    return room?.getMyMembership() === 'join' && isCompanyEmojiRoom(room) ? packsOfRoom(room, undefined, use, companyName).map((p) => ({ ...p, name: p.name === room.name ? companyName : p.name })) : [];
  }
  const everywhere = (client.getAccountData('im.ponies.emote_rooms' as never)?.getContent().rooms ?? {}) as Record<string, Record<string, unknown>>;
  return Object.entries(everywhere).flatMap(([roomId, keys]) => {
    const room = client.getRoom(roomId);
    return room?.getMyMembership() === 'join' ? packsOfRoom(room, Object.keys(keys ?? {}), use, unknownName) : [];
  });
}

/**
 * The company emoji channel (16, "The company's emoji"): named only by the admin service; joined in
 * the background, left again when its creation type isn't the company emoji's, older ones left.
 */
export async function joinCompanyEmoji(client: MatrixClient): Promise<void> {
  if (!inCompany(client)) return;
  const id = (await admin<{ emojiRoomId: string | null }>(client, '/me').catch(() => undefined))?.emojiRoomId ?? null;
  setCompanyEmojiRoom(id);
  for (const room of client.getRooms())
    if (room.getMyMembership() === 'join' && room.roomId !== id && isCompanyEmojiRoom(room)) void client.leave(room.roomId).catch(() => {});
  if (!id) return;
  if (client.getRoom(id)?.getMyMembership() !== 'join') await client.joinRoom(id).catch(() => {});
  const create = await client.getStateEvent(id, EventType.RoomCreate, '').catch(() => undefined);
  if (create?.type !== COMPANY_TYPE) {
    setCompanyEmojiRoom(null);
    void client.leave(id).catch(() => {});
  }
}
